/**
 * Utility for extracting Google Analytics data from cookies
 * Used to pass client_id and session_id to Zoho CRM
 */

/**
 * Get a cookie value by name
 * @param name - cookie name
 * @returns cookie value or null
 */
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null

  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)

  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null
  }

  return null
}

/**
 * Validate the extracted value
 * Only digits, dots, and the $ symbol are allowed
 * Length is capped to guard against overflow
 * @param value - value to validate
 * @returns true if the value is valid
 */
function isValidValue(value: string | null): boolean {
  if (!value || typeof value !== 'string') return false
  if (value.length > 100) return false
  return /^[\d.$]+$/.test(value)
}

/**
 * Extract client_id from the _ga cookie
 * Strips the GAx.x. prefix and returns just the tail
 * @returns client_id or null
 * @example
 * // cookie: _ga=GA1.1.1405948988.1764142353
 * getClientId() // returns "1405948988.1764142353"
 */
export function getClientId(): string | null {
  try {
    const gaCookie = getCookie('_ga')

    if (!gaCookie) {
      return null
    }

    const clientId = gaCookie.replace(/^GA\d+\.\d+\./, '')

    if (!isValidValue(clientId)) {
      console.warn('[GA Cookie] Invalid client_id format:', clientId)
      return null
    }

    return clientId
  } catch (error) {
    console.error('[GA Cookie] Error extracting client_id:', error)
    return null
  }
}

/**
 * Extract session_id from the _ga_* cookie
 * Finds the cookie starting with _ga_ (but not equal to _ga)
 * Strips the GSx.x. prefix and cuts off everything after the first dot/separator
 * @returns session_id or null
 * @example
 * // cookie: _ga_TYB9026PVG=GS2.1.1764142353.1.1.16000$o1...
 * getSessionId() // returns "1764142353"
 */
export function getSessionId(): string | null {
  try {
    if (typeof document === 'undefined') return null

    const cookies = document.cookie.split(';')
    let maxSessionId = 0
    let bestCookie = null

    // Look for a cookie starting with _ga_, but not equal to _ga
    // And pick the one with the highest session_id (the freshest session)
    for (let cookie of cookies) {
      cookie = cookie.trim()

      if (cookie.startsWith('_ga_') && !cookie.startsWith('_ga=')) {
        const [, value] = cookie.split('=')

        if (!value) continue

        // Strip the GSx.x. prefix
        const sessionValue = value.replace(/^GS\d+\.\d+\./, '')

        // Extract the first block of digits (session_id)
        const match = sessionValue.match(/(\d+)/)

        if (match && match[1]) {
          const sessionId = match[1]
          const timestamp = parseInt(sessionId, 10)

          // Validate and pick the maximum
          if (isValidValue(sessionId) && !isNaN(timestamp)) {
            if (timestamp > maxSessionId) {
              maxSessionId = timestamp
              bestCookie = sessionId
            }
          }
        }
      }
    }

    return bestCookie
  } catch (error) {
    console.error('[GA Cookie] Error extracting session_id:', error)
    return null
  }
}

export interface GAParameters {
  client_id: string | null
  session_id: string | null
}

/**
 * Get both GA parameters to send to Zoho
 * @returns object with client_id and session_id
 */
export function getGAParameters(): GAParameters {
  return {
    client_id: getClientId(),
    session_id: getSessionId()
  }
}

/**
 * Inject GA parameters (client_id, session_id) into axios request config data.
 * Used as a shared interceptor logic across all axios instances.
 * Only applies to POST requests to /orders or /zoho/createRequest endpoints.
 * @param config - axios request config
 */
export function injectGAParams(config: {
  method?: string
  url?: string
  data?: Record<string, unknown>
}): void {
  const shouldAddGAParams =
    config.method === 'post' &&
    config.url &&
    (config.url.includes('/orders') || config.url.includes('/zoho/createRequest'))

  if (shouldAddGAParams && config.data && typeof config.data === 'object') {
    const gaParams = getGAParameters()

    if (gaParams.client_id) {
      config.data.client_id = gaParams.client_id
    }
    if (gaParams.session_id) {
      config.data.session_id = gaParams.session_id
    }
  }
}
